import type { CouponType, IconName } from '@cencosud-cencommerce/paris-uikit';
import type {
  Price,
  ShippingData
} from '@cencosud-cencommerce/paris-uikit/dist/utils/types/PurchaseSummary.type';

import type { CartItem } from '@/modules/cart/domain/CartData';
import type { CartLineItem } from '@/modules/cart/domain/CartLineItem';
import type { IUserCustomerData } from '@/modules/perseo-billing/hooks/order-confirmation/use-map-order-confirmation';
import { EPricePaymentMethod } from '@/modules/perseo-billing/interfaces/src/bff-web/billing/interfaces/orderV2';
import { ShippingTypeEnum } from '@/modules/product/domain/enumsTypes';
import { Collections } from '@/modules/product/infrastructure/dto/HttpProductDetailsDto';
import { priceDtoToPriceString } from '@/modules/shared/mappers/priceDtoToPriceString';
import type { PriceDto } from '@/modules/shared/types/PriceDto';
import { getTotalCost } from '@/modules/shared/utils/getTotalCost';
import { isWeddingFlow } from '@/modules/shared/utils/isWeddingFlow';

import type { Cart } from '../../domain/Cart';
import type { CartData } from '../../domain/CartData';
import type { CartProductOption } from '../../domain/CartProductOption';

import { discountToPercentage } from './../../../shared/mappers/priceDtoToPriceString';
import type { DeliveryInfo } from './../../domain/Cart';

const UNIQUE_COLORS = ['Color Unico', 'Único Color'];
const UNIQUE_SIZES = ['Talla Unica', 'Tamano Unico'];

const isWedding = isWeddingFlow();

const getLineItemPrices = (lineItem: CartLineItem): Price[] => {
  const prices = [] as Price[];

  const lineItemPrices = lineItem?.variant?.prices;
  if (lineItemPrices?.paymentMethod) {
    const method = lineItemPrices?.paymentMethod?.method;
    const discountOnRegular = lineItemPrices?.paymentMethod?.discountOnRegular;
    prices.push({
      price: priceDtoToPriceString(lineItemPrices?.paymentMethod?.value),
      discountPercentage: discountToPercentage(discountOnRegular),
      isCencoPayPrice: method === EPricePaymentMethod.CENCO_PAY,
      isCencoPrice: method === EPricePaymentMethod.CENCOSUD_CARD
    });
  }

  if (lineItemPrices?.offer)
    prices.push({
      price: priceDtoToPriceString(lineItemPrices?.offer?.value),
      discountPercentage: discountToPercentage(
        lineItemPrices?.offer?.discountOnRegular
      )
    });

  prices.push({
    price: priceDtoToPriceString(lineItemPrices?.regular?.value),
    isStrikeThrough: !!lineItemPrices?.offer
  });

  return prices;
};

const lineItemtoCartItem = (
  lineItem: CartLineItem,
  customer?: Partial<IUserCustomerData>
): CartItem => {
  const name = lineItem.name;
  let description = `Cantidad: ${lineItem.quantity}`;

  const sizeAttribute = lineItem.variant.attributes?.find(
    (attribute) =>
      attribute.name === 'size' &&
      !UNIQUE_SIZES.includes(attribute.value as string)
  );

  if (sizeAttribute) description += ` | ${sizeAttribute.value}`;

  const colorAttribute = lineItem.variant.attributes?.find(
    (attribute) =>
      attribute.name === 'color' &&
      !UNIQUE_COLORS.includes(attribute.value as string)
  );

  if (colorAttribute) description += ` | ${colorAttribute.value}`;

  const conditionAttribute = lineItem.variant.attributes?.find(
    (attribute) =>
      attribute.name === 'condicionProducto' &&
      typeof attribute.value === 'object' &&
      attribute.value !== null &&
      !Array.isArray(attribute.value) &&
      'key' in attribute.value &&
      attribute.value.key === 'segundaMano'
  );
  const prices = getLineItemPrices(lineItem);
  const onlyRegularPrice = prices?.filter(
    (price) => !price?.isCencoPrice && !price?.isCencoPayPrice
  );

  const isCollab =
    !!lineItem?.variant?.collections?.includes(
      Collections.TAG_BENEFICIOS_CRUZADOS_COLABORADORES
    ) && !!customer?.isColabCustomer;

  const cartPrices = isWedding ? onlyRegularPrice : prices;
  return {
    name,
    description1: description,
    description2:
      conditionAttribute &&
      typeof conditionAttribute.value === 'object' &&
      conditionAttribute.value !== null &&
      !Array.isArray(conditionAttribute.value) &&
      'label' in conditionAttribute.value
        ? conditionAttribute.value.label
        : undefined,
    image: lineItem.variant.images?.[0].url,
    prices: cartPrices,
    tags: {
      isPresale: !!lineItem.variant.isPreSale,
      isCollab
    }
  };
};

const findIconName = (
  productOptionType: 'service' | 'warranty',
  productOptionName: string
): IconName => {
  if (productOptionType === 'warranty') return 'il-product-warranty-alt';

  switch (true) {
    case productOptionName.toLowerCase().includes('instalacion'):
      return 'il-installation-service';
    case productOptionName.toLowerCase().includes('armado'):
      return 'il-assembly-service';
    default:
      return 'il-recycle';
  }
};

const productOptionToCartItem = (
  productOption: CartProductOption
): CartItem => {
  let description = `Cantidad: ${productOption.quantity}`;
  const years = productOption.warrantyTime
    ? productOption.warrantyTime / 12
    : 0;
  if (years > 0) {
    description += `| Garantía: ${years} año${years > 1 ? 's' : ''}`;
  }

  const iconName = findIconName(productOption.addonType, productOption.name);

  return {
    name: productOption.name,
    description1: description,
    iconName,
    prices: [{ price: priceDtoToPriceString(productOption.totalPrice) }]
  };
};

export const deliveryInfoToShippingData = (
  deliveryInfo?: DeliveryInfo
): ShippingData => {
  if (!deliveryInfo) {
    return {
      shippingItems: [],
      shippingCost: '$0',
      shippingTotal: '$0'
    };
  }

  const shippingItems: ShippingData['shippingItems'] = [];

  deliveryInfo.deliveryGroups.forEach((delivery, index) => {
    const shippingOption = delivery.shippingOption;
    let name =
      shippingOption.shippingType === ShippingTypeEnum.COLLECT
        ? 'Retiro en tienda'
        : 'Entrega';
    const amount = +(shippingOption?.shipmentCostTotal ?? 0);
    if (deliveryInfo.deliveryGroups.length > 1) {
      name = `${name} ${index + 1}`;
    }

    shippingItems.push({
      name,
      amount: amount
        ? `${priceDtoToPriceString({ centAmount: amount, currencyCode: 'CLP' })}`
        : amount
    });
  });

  const discounts: { name: string; amount: number }[] = [];

  deliveryInfo.deliveryGroups.forEach((delivery) => {
    delivery.includedDiscounts.forEach((discount) => {
      const index = discounts.findIndex(
        (item) => item.name === discount.name['es-CL']
      );
      if (index !== -1) {
        discounts[index].amount += discount.discountedAmount.centAmount;
      } else {
        discounts.push({
          name: discount.name['es-CL'],
          amount: discount.discountedAmount.centAmount
        });
      }
    });
  });

  deliveryInfo.includedDiscounts.forEach((discount) => {
    shippingItems.push({
      name: discount.name['es-CL'],
      amount: `-${priceDtoToPriceString(discount.discountedAmount)}`,
      type: 'discount'
    });
  });

  discounts.map((discount) => {
    shippingItems.push({
      name: discount.name,
      amount: `-${priceDtoToPriceString({ centAmount: discount.amount, currencyCode: 'CLP' })}`,
      type: 'discount'
    });
  });

  const shippingTotal = priceDtoToPriceString(deliveryInfo.totalPrice);

  return {
    shippingItems,
    shippingTotal,
    shippingCost: shippingTotal
  };
};

export const cartToCartData = (
  cart: Cart,
  customer?: Partial<IUserCustomerData>
): CartData => {
  const productOptionsBySku = cart.productOptions.reduce(
    (acc, productOption) => {
      if (!acc[productOption.productSku]) {
        acc[productOption.productSku] = [];
      }
      acc[productOption.productSku].push(productOption);
      return acc;
    },
    {} as Record<string, CartProductOption[]>
  );

  const cartItems = cart.lineItems.flatMap((lineItem) => {
    const lineItemCartItem = lineItemtoCartItem(lineItem, customer);

    const associatedProductOptions =
      productOptionsBySku[lineItem.variant.sku] || [];

    const productOptionCartItems = associatedProductOptions.map(
      (productOption) => productOptionToCartItem(productOption)
    );

    return [lineItemCartItem, ...productOptionCartItems];
  });

  const shippingAddress = cart.shippingAddress;
  const region = Number(shippingAddress?.region) ?? 0;

  return {
    id: cart.id,
    shippingAddress: shippingAddress
      ? {
          ...shippingAddress,
          region: region
        }
      : undefined,
    cartItems,
    productsCost: priceDtoToPriceString(getTotalCost(cart))
  };
};

export const cartDiscountsToCoupons = (
  cart?: Cart
): CouponType[] | undefined => {
  if (!cart) return undefined;

  const couponName = cart?.discountCodes?.[0]?.discountCode?.obj?.name['es-CL'];
  const couponId = cart?.discountCodes?.[0]?.discountCode?.id;

  if (cart.discountOnTotalPrice) {
    return cart?.discountCodes?.map((discount) => {
      const matchedDiscount =
        cart?.discountOnTotalPrice?.includedDiscounts.find((includedDiscount) =>
          discount.discountCode?.obj?.cartDiscounts?.some(
            (cartDiscount) => cartDiscount.id === includedDiscount.discount.id
          )
        );

      if (matchedDiscount) {
        return {
          discountAmount: priceDtoToPriceString(
            matchedDiscount?.discountedAmount
          ),
          couponName,
          couponId
        } as CouponType;
      }
      return {} as CouponType;
    });
  }

  if (
    !cart.discountOnTotalPrice &&
    cart.deliveryInfo?.includedDiscounts?.length
  ) {
    return [
      {
        discountAmount: priceDtoToPriceString(
          cart.deliveryInfo.includedDiscounts[0].discountedAmount
        ),
        couponName,
        couponId
      }
    ] as CouponType[];
  }

  if (
    !cart.discountOnTotalPrice &&
    !cart.deliveryInfo?.includedDiscounts?.length
  ) {
    const couponAmount = { centAmount: 0, currencyCode: 'CLP' };

    cart.deliveryInfo?.deliveryGroups.forEach((delivery) => {
      couponAmount.centAmount +=
        delivery?.includedDiscounts?.[0]?.discountedAmount?.centAmount || 0;
    });

    if (couponAmount.centAmount) {
      return [
        {
          couponId,
          couponName,
          discountAmount: priceDtoToPriceString(couponAmount as PriceDto)
        }
      ] as CouponType[];
    }
  }

  return undefined;
};
